import React, {useMemo} from "react";
import {Path, useFormContext, Validate} from "react-hook-form";
import _ from "lodash";
import {
    AuthorizationInfo,
    ClientReportSubmitClientOfferHourInput,
    defaultClinicalRecommendationHoursFromAuth,
    defaultServiceTypesByAuthTier1,
    HourField,
    ReportServiceType,
    serviceTypeMappedToHourFields,
} from "../../domain";
import {TreatmentHoldInput} from "./TreatmentHoldFormModal";
import {hourInputValidationRules, offeredHourRangeMap, shouldShowHourInputs, stopNonNumericInput} from "../../form-components/auth-hours/view-domains";
import {OfferedHourInput} from "../../form-components/auth-hours/OfferedHourInput";

type OfferHourField = keyof Pick<
    TreatmentHoldInput,
    "offeredParaprofessionalHours" | "offeredMidLevelHours" | "offeredHighLevelHours" | "offeredGroupCareHours"
>;

type AuthClinicalHours = ReturnType<typeof defaultClinicalRecommendationHoursFromAuth>;

function getPermittedHourFields(serviceTypes: ReportServiceType[]): HourField[] {
    return _.flatMap(serviceTypes, (serviceType) => serviceTypeMappedToHourFields.get(serviceType) ?? []);
}


export function validateTreatmentHoldOfferedAgainstAuthRange(
    offeredHour: number | null,
    field: OfferHourField,
    offeredHourLabel: string,
    authHours: AuthClinicalHours | null | undefined,
    serviceTypes: ReportServiceType[],
): true | string {
    if (offeredHour == null || authHours == null) {
        return true;
    }

    const minMaxRanges = offeredHourRangeMap.get(field as keyof ClientReportSubmitClientOfferHourInput);
    if (minMaxRanges == null || minMaxRanges.length === 0) {
        return true;
    }

    const permittedFields = getPermittedHourFields(serviceTypes);

    let isWithinRange = false;
    let hasABound = false;
    let failedBelowMin = false;

    for (const [minField, maxField] of minMaxRanges) {
        if (!(permittedFields.includes(minField) && permittedFields.includes(maxField))) {
            continue;
        }

        const min = authHours[minField as keyof AuthClinicalHours] as number | null | undefined;
        const max = authHours[maxField as keyof AuthClinicalHours] as number | null | undefined;

        if (min == null && max == null) {
            continue;
        }

        // Null auth min → treat as 0 (same idea as referral HoursInput default min)
        const effectiveMin = min ?? 0;

        if (max != null) {
            hasABound = true;
            // Inverted clinical range (e.g. min=1, max=0) cannot accept an offer
            if (max < effectiveMin) {
                continue;
            }
            if (effectiveMin >= 0 && max >= 0 && effectiveMin <= offeredHour && offeredHour <= max) {
                isWithinRange = true;
            } else if (offeredHour < effectiveMin) {
                failedBelowMin = true;
            }
        } else {
            // Max missing: only enforce minimum
            hasABound = true;
            if (offeredHour >= effectiveMin) {
                isWithinRange = true;
            } else {
                failedBelowMin = true;
            }
        }

        if (isWithinRange) {
            return true;
        }
    }

    if (isWithinRange) {
        return true;
    }

    if (hasABound) {
        if (failedBelowMin) {
            return "Offered Hours cannot be less than the Minimum required hours for the current Authorization";
        }
        return `${offeredHourLabel} must be within range of Clinically Recommended Hours`;
    }

    return true;
}

type TreatmentHoldOfferedHourInputProps = {
    field: OfferHourField;
    label: string;
    disabled: boolean;
    serviceTypes: ReportServiceType[];
    authHours: AuthClinicalHours;
};

function TreatmentHoldOfferedHourInput({field, label, disabled, serviceTypes, authHours}: TreatmentHoldOfferedHourInputProps) {
    const {
        formState: {errors},
        register,
        trigger,
    } = useFormContext<TreatmentHoldInput>();

    if (!shouldShowHourInputs(serviceTypes, [field as HourField])) {
        return null;
    }

    const validateRange: Validate<number | null> = (offeredHour) =>
        validateTreatmentHoldOfferedAgainstAuthRange(offeredHour, field, label, authHours, serviceTypes);

    const registered = register(field, {
        ...hourInputValidationRules,
        required: {value: true, message: "This field is required."},
        validate: {
            ...hourInputValidationRules.validate,
            offeredAgainstAuthRange: validateRange,
        },
    });
    const defaultOnChange = registered.onChange;
    registered.onChange = async (event) => {
        if (await stopNonNumericInput(event)) {
            return;
        }
        await defaultOnChange(event);
        trigger(field as Path<TreatmentHoldInput>);
    };

    return <OfferedHourInput label={label} disabled={disabled} registered={registered} error={errors[field]?.message}/>;
}

type Props = {
    disabled: boolean;
    authorization: AuthorizationInfo;
};

export function TreatmentHoldOfferHoursInput({disabled, authorization}: Props) {
    const {watch} = useFormContext<TreatmentHoldInput>();
    const formServiceTypes = watch("serviceType") as ReportServiceType[];

    const serviceTypes = useMemo(() => {
        const fromForm = (formServiceTypes ?? []).filter((s): s is ReportServiceType => s != null);
        if (!_.isEmpty(fromForm)) {
            return fromForm;
        }
        return defaultServiceTypesByAuthTier1(authorization);
    }, [formServiceTypes, authorization]);

    const authHours = useMemo(
        () => defaultClinicalRecommendationHoursFromAuth(authorization),
        [authorization],
    );

    const hasAnyHourField = shouldShowHourInputs(serviceTypes, [
        "offeredParaprofessionalHours",
        "offeredMidLevelHours",
        "offeredHighLevelHours",
        "offeredGroupCareHours",
    ]);

    if (!hasAnyHourField) {
        return null;
    }

    return (
        <div className="w-full text-base pb-2">
            <div className="pb-2">
                <span className="font-bold">Offer:</span>
                &nbsp;
                (hours per month)
            </div>
            <div className="w-full flex flex-wrap">
                <TreatmentHoldOfferedHourInput
                    field="offeredParaprofessionalHours"
                    label="Paraprofessional Hours"
                    disabled={disabled}
                    serviceTypes={serviceTypes}
                    authHours={authHours}
                />
                <TreatmentHoldOfferedHourInput
                    field="offeredMidLevelHours"
                    label="Mid-level Hours"
                    disabled={disabled}
                    serviceTypes={serviceTypes}
                    authHours={authHours}
                />
                <TreatmentHoldOfferedHourInput
                    field="offeredHighLevelHours"
                    label="High-level Hours"
                    disabled={disabled}
                    serviceTypes={serviceTypes}
                    authHours={authHours}
                />
                <TreatmentHoldOfferedHourInput
                    field="offeredGroupCareHours"
                    label="Group Care Hours"
                    disabled={disabled}
                    serviceTypes={serviceTypes}
                    authHours={authHours}
                />
            </div>
        </div>
    );
}
